import { cn } from "@/utils/cn";
import { Loader2, X } from "lucide-react";
import { useEffect, useRef } from "react";
import type { PipelineStep, SearchContext } from "../types";
import { LABEL, PANEL } from "./styles";

/** Accent per pipeline step so successive filterings read as distinct
 * subsets. Cycles if a pipeline grows past the palette. */
const STEP_COLORS: Array<{ dot: string; dotHover: string; text: string }> = [
  { dot: "bg-emerald-500", dotHover: "hover:bg-emerald-600", text: "text-emerald-600" },
  { dot: "bg-sky-500", dotHover: "hover:bg-sky-600", text: "text-sky-600" },
  { dot: "bg-amber-500", dotHover: "hover:bg-amber-600", text: "text-amber-600" },
  { dot: "bg-violet-500", dotHover: "hover:bg-violet-600", text: "text-violet-600" },
  { dot: "bg-rose-500", dotHover: "hover:bg-rose-600", text: "text-rose-600" },
  { dot: "bg-teal-500", dotHover: "hover:bg-teal-600", text: "text-teal-600" },
];

const stepColor = (index: number) => STEP_COLORS[index % STEP_COLORS.length];

/** Shared dot geometry: every grid (base + steps) uses identical size, gap,
 * and padding so dot columns line up vertically across all rows. */
const GRID_CLASS =
  "flex max-h-16 flex-wrap content-start gap-1.5 overflow-y-auto p-1";
const DOT_CLASS = "h-2.5 w-2.5 shrink-0 rounded-full transition-all";

/**
 * Full-width bar above the output panel showing the live result set.
 *
 * Each dot is one document from the last search, in result order. The dot
 * for the paper currently in view is emphasized (scroll-synced with the
 * list below); clicking a dot autoscrolls the list to that document. Every
 * grep/filter/map applied to the set appends a step: its command on one
 * line, and below it a dot grid aligned with the base set showing which
 * papers survived — the visible state of the server-side `s_…` handle.
 */
export const ResultSetPanel = ({
  context,
  steps,
  activeId,
  highlight,
  restoring,
  onDotClick,
  onRemoveStep,
  onReset,
}: {
  context: SearchContext;
  steps: PipelineStep[];
  /** document_id of the paper currently scrolled into view. */
  activeId: string | null;
  /** Hits of the step whose results are on screen — dims non-matching dots. */
  highlight: Record<string, number> | null;
  /** True while a removed filter step is being rolled back server-side. */
  restoring: boolean;
  onDotClick: (docId: string) => void;
  /** Remove step `id` and everything after it (rolls the set back). */
  onRemoveStep: (id: number) => void;
  /** Drop the whole set and start fresh. */
  onReset: () => void;
}) => {
  const papers = context.papers;
  const gridRef = useRef<HTMLDivElement>(null);

  // The base-grid overlay takes the color of the step whose hits are being
  // shown, so the top grid always echoes one of the step rows below.
  const highlightStepIdx = highlight
    ? steps.findIndex((s) => s.hits === highlight)
    : -1;
  const highlightColor = stepColor(highlightStepIdx >= 0 ? highlightStepIdx : 0);

  // With many results the dot grid scrolls; keep the active dot visible so
  // the highlight follows along as the list below is scrolled.
  useEffect(() => {
    if (!activeId || !gridRef.current) return;
    gridRef.current
      .querySelector(`[data-dot="${CSS.escape(activeId)}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeId]);

  return (
    <section
      aria-label="Live result set"
      className={`${PANEL} bg-zinc-900/[0.03] p-4`}
    >
      <div className="flex min-w-0 items-baseline gap-2">
        <span className={`${LABEL} shrink-0`}>Result set</span>
        <span
          className="min-w-0 shrink truncate text-sm italic text-zinc-800"
          title={context.query}
        >
          “{context.query}”
        </span>
        <span className="shrink-0 font-mono text-3xs text-zinc-400">
          {context.resultsId}
        </span>
        <span className="shrink-0 text-3xs text-zinc-400">
          {papers.length} docs
        </span>
        {restoring ? (
          <span className="ml-auto flex shrink-0 items-center gap-1 text-3xs text-zinc-500">
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
            Rolling back…
          </span>
        ) : (
          <span
            className="ml-auto hidden min-w-0 truncate text-3xs text-zinc-400 md:block"
            title="Persists server-side — grep and map keep operating on it until you search again"
          >
            Persists server-side — grep and map keep operating on it until you
            search again
          </span>
        )}
        <button
          type="button"
          onClick={onReset}
          disabled={restoring}
          title="Clear the result set and start over"
          aria-label="Clear the result set"
          className="shrink-0 self-center rounded p-0.5 text-zinc-300 transition-colors hover:bg-zinc-100 hover:text-zinc-600 disabled:opacity-40"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>

      {/* Base set: one dot per document, scroll-synced with the list below.
          Scrolls vertically when the set outgrows two rows; the padding
          leaves room for the active dot's ring so it isn't clipped. */}
      <div ref={gridRef} role="list" className={cn("mt-1.5", GRID_CLASS)}>
        {papers.map((paper, i) => {
          const id = paper.document_id;
          const isActive = id === activeId;
          const matched = highlight ? (highlight[id] ?? 0) > 0 : null;
          return (
            <button
              key={id}
              type="button"
              role="listitem"
              data-dot={id}
              onClick={() => onDotClick(id)}
              title={`#${i + 1} ${id}${paper.title ? ` — ${paper.title}` : ""}`}
              aria-label={`Scroll to result ${i + 1}: ${paper.title ?? id}`}
              aria-current={isActive || undefined}
              className={cn(
                DOT_CLASS,
                matched === null
                  ? "bg-zinc-300 hover:bg-zinc-500"
                  : matched
                    ? cn(highlightColor.dot, highlightColor.dotHover)
                    : "bg-zinc-200 hover:bg-zinc-400",
                isActive &&
                "ring-2 ring-zinc-900 ring-offset-1 ring-offset-white",
                isActive && matched === null && "bg-zinc-900",
              )}
            />
          );
        })}
      </div>

      {/* Pipeline history: each step is its command on one line, then a
          full-width dot grid using the same geometry as the base set, so
          the same paper occupies the same position in every row. */}
      {steps.length > 0 && (
        <ol className="mt-2 space-y-2.5 border-t border-zinc-100 pt-2">
          {steps.map((step, stepIdx) => {
            const color = stepColor(stepIdx);
            const matchedCount = papers.filter(
              (p) => (step.hits[p.document_id] ?? 0) > 0,
            ).length;
            return (
              <li key={step.id}>
                <div className="flex min-w-0 items-baseline gap-1.5">
                  <span
                    className={cn(
                      "h-2 w-2 shrink-0 self-center rounded-full",
                      color.dot,
                    )}
                    aria-hidden
                  />
                  <span className="shrink-0 text-3xs tabular-nums text-zinc-400">
                    {stepIdx + 1}.
                  </span>
                  <code
                    className="min-w-0 flex-1 truncate font-mono text-3xs text-zinc-600"
                    title={`paperclip ${step.label}`}
                  >
                    {step.label}
                  </code>
                  <span
                    className={cn(
                      "shrink-0 text-3xs tabular-nums",
                      color.text,
                    )}
                  >
                    {matchedCount}/{papers.length}{" "}
                    {step.kind === "map"
                      ? "answered"
                      : step.kind === "filter"
                        ? "kept"
                        : "matched"}
                  </span>
                  <button
                    type="button"
                    onClick={() => onRemoveStep(step.id)}
                    disabled={restoring}
                    title={
                      stepIdx === 0
                        ? "Remove this step (and any after it) — back to the raw result set"
                        : `Remove this step (and any after it) — back to step ${stepIdx}`
                    }
                    aria-label={`Remove step ${stepIdx + 1}`}
                    className="shrink-0 self-center rounded p-0.5 text-zinc-300 transition-colors hover:bg-zinc-100 hover:text-zinc-600 disabled:opacity-40"
                  >
                    <X className="h-3 w-3" aria-hidden />
                  </button>
                </div>
                <div role="list" className={cn("mt-1", GRID_CLASS)}>
                  {papers.map((paper, i) => {
                    const id = paper.document_id;
                    const hit = (step.hits[id] ?? 0) > 0;
                    const isActive = id === activeId;
                    return (
                      <button
                        key={id}
                        type="button"
                        role="listitem"
                        onClick={() => onDotClick(id)}
                        title={`#${i + 1} ${id}: ${hit
                          ? step.kind === "map"
                            ? "answered"
                            : step.kind === "filter"
                              ? "kept"
                              : `${step.hits[id]} match(es)`
                          : step.kind === "map"
                            ? "no answer"
                            : step.kind === "filter"
                              ? "dropped"
                              : "no match"
                          }`}
                        aria-label={`Scroll to result ${i + 1}: ${paper.title ?? id}`}
                        className={cn(
                          DOT_CLASS,
                          hit
                            ? cn(color.dot, color.dotHover)
                            : "bg-zinc-200 hover:bg-zinc-400",
                          isActive &&
                          "ring-2 ring-zinc-900 ring-offset-1 ring-offset-white",
                        )}
                      />
                    );
                  })}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
};
